import { saveAs } from 'file-saver';
import { PaperData, Flashcard } from '../types/paper';

/**
 * Export Flashcards to Anki-compatible Tab-Separated Values (TSV)
 */
export function exportToAnkiTSV(paper: PaperData, selectedCards?: Flashcard[]) {
  const cards = selectedCards || paper.flashcards;
  
  // Header comment for Anki import configuration
  let tsvContent = `#separator:tab\n#html:true\n#tags column:3\n`;

  cards.forEach(card => {
    // Escape tabs and line breaks for Anki TSV compliance
    const front = card.question
      .replace(/\t/g, ' ')
      .replace(/\n/g, '<br>');

    let backHtml = card.answer.replace(/\t/g, ' ').replace(/\n/g, '<br>');

    if (card.formula) {
      backHtml += `<br><br><b>Formula:</b><br>\\(${card.formula.replace(/\n/g, ' ')}\\)`;
    }

    if (card.codeSnippet) {
      backHtml += `<br><br><pre><code>${card.codeSnippet.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`;
    }

    if (card.figureUrl) {
      backHtml += `<br><br><img src="${card.figureUrl}" alt="Paper Figure" style="max-width:100%; border-radius:8px;"><br><i>${card.figureCaption || ''}</i>`;
    }

    const tags = ['ScholarWiki', paper.title.replace(/\s+/g, '_'), ...card.tags].join(' ');

    tsvContent += `${front}\t${backHtml}\t${tags}\n`;
  });

  const blob = new Blob([tsvContent], { type: 'text/tab-separated-values;charset=utf-8' });
  const filename = `${paper.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}_anki_flashcards.tsv`;
  saveAs(blob, filename);
}

/**
 * Export complete Paper Knowledge Wiki + Flashcards to Markdown (.md)
 */
export function exportToMarkdown(paper: PaperData) {
  let md = `---
title: "${paper.title}"
authors: [${paper.authors.map(a => `"${a}"`).join(', ')}]
year: "${paper.year}"
arxiv_id: "${paper.arxivId || ''}"
exported_at: "${new Date().toISOString()}"
tags: [scholar-wiki, paper-summary, flashcards]
---\n\n`;

  md += `# ${paper.title}\n\n`;
  md += `**Authors**: ${paper.authors.join(', ')}  \n`;
  md += `**Year**: ${paper.year} | **arXiv**: [${paper.arxivId || 'N/A'}](https://arxiv.org/abs/${paper.arxivId || ''})  \n\n`;

  md += `## Abstract\n> ${paper.abstract}\n\n`;

  md += `---\n\n## 🧬 Concept Lineage & Ancestry Graph\n\n`;
  paper.lineageGraph.nodes.forEach(node => {
    md += `### [${node.type.toUpperCase()}] ${node.label} (${node.year})\n`;
    if (node.authors) md += `*Authors*: ${node.authors}\n`;
    md += `${node.description}\n`;
    md += `**Key Innovation**: ${node.keyInnovation}\n\n`;
  });

  md += `---\n\n## 📚 LLM-Wiki Core Concepts\n\n`;
  paper.concepts.forEach(concept => {
    md += `### ${concept.title} \`[${concept.category}]\` \n\n`;
    md += `**Definition**: ${concept.definition}\n\n`;

    if (concept.mathFormulas.length > 0) {
      md += `#### Mathematical Formulas\n`;
      concept.mathFormulas.forEach(f => {
        md += `* **${f.label}**:\n  $$${f.latex}$$\n  *Explanation*: ${f.explanation}\n\n`;
      });
    }

    md += `#### Core Intuition\n${concept.intuition}\n\n`;

    if (concept.keyNuances.length > 0) {
      md += `#### Key Nuances\n`;
      concept.keyNuances.forEach(n => md += `- ${n}\n`);
      md += `\n`;
    }

    if (concept.codeSnippet) {
      md += `#### Code Implementation (${concept.codeSnippet.language})\n`;
      md += `\`\`\`${concept.codeSnippet.language}\n${concept.codeSnippet.code}\n\`\`\`\n`;
      md += `*${concept.codeSnippet.explanation}*\n\n`;
    }

    if (concept.ancestors.length > 0) {
      md += `#### Lineage Ancestors\n`;
      concept.ancestors.forEach(a => {
        md += `- **${a.concept}** (${a.paperTitle} - ${a.authorsYear}): ${a.relationship}\n`;
      });
      md += `\n`;
    }
  });

  md += `---\n\n## 🎴 High-Yield Spaced Repetition Flashcards\n\n`;
  paper.flashcards.forEach((card, index) => {
    md += `### Flashcard ${index + 1}: ${card.question}\n\n`;
    md += `**Difficulty**: \`${card.difficulty || 'medium'}\` | **Tags**: \`${card.tags.join(', ')}\`  \n\n`;
    md += `**Answer**:\n${card.answer}\n\n`;

    if (card.formula) {
      md += `**Formula**:\n$$${card.formula}$$\n\n`;
    }

    if (card.figureUrl) {
      md += `![${card.figureCaption || 'Paper Figure'}](${card.figureUrl})\n*${card.figureCaption || ''}*\n\n`;
    }

    md += `---\n\n`;
  });

  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const filename = `${paper.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}_wiki_flashcards.md`;
  saveAs(blob, filename);
}
